import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import Button from '~/components/buttons/Button'
import LeftArrowIcon from '~/components/icons/LeftArrowIcon'
import RightArrowIcon from '~/components/icons/RightArrowIcon'
import Select from '~/components/Select'
import cs from './pagination_controls.scss'

export const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

type PageSizeOption = {
  key: string
  label: string
  value: number
}

const PAGE_SIZE_SELECT_OPTIONS: PageSizeOption[] = PAGE_SIZE_OPTIONS.map(size => ({
  key: size.toString(),
  label: size.toString(),
  value: size,
}))

type PaginationControlsProps = {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

export default function PaginationControls({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: PaginationControlsProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1)
  const displayPage = Math.min(page, totalPages)
  const hasResults = total > 0
  const start = hasResults ? (displayPage - 1) * pageSize + 1 : 0
  const end = hasResults ? Math.min(total, displayPage * pageSize) : 0
  const activeOption =
    PAGE_SIZE_SELECT_OPTIONS.find(option => option.value === pageSize) ||
    PAGE_SIZE_SELECT_OPTIONS[0]
  const [isEditingPage, setIsEditingPage] = useState(false)
  const [pageInputValue, setPageInputValue] = useState(displayPage.toString())
  const pageInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditingPage) {
      setPageInputValue(displayPage.toString())
    }
  }, [displayPage, isEditingPage])

  useEffect(() => {
    if (isEditingPage) {
      pageInputRef.current?.select()
    }
  }, [isEditingPage])

  const handlePrevious = () => {
    if (displayPage > 1) {
      onPageChange(displayPage - 1)
    }
  }

  const handleNext = () => {
    if (displayPage < totalPages && hasResults) {
      onPageChange(displayPage + 1)
    }
  }

  const handlePageIndicatorClick = () => {
    if (!hasResults) {
      return
    }

    setIsEditingPage(true)
    setPageInputValue(displayPage.toString())
  }

  const cancelPageEdit = () => {
    setIsEditingPage(false)
    setPageInputValue(displayPage.toString())
  }

  const commitPageEdit = () => {
    const parsedPage = Number(pageInputValue)
    setIsEditingPage(false)
    if (!Number.isInteger(parsedPage) || parsedPage < 1 || parsedPage > totalPages) {
      // If invalid page number, ignore
      return
    }

    if (parsedPage !== displayPage) {
      onPageChange(parsedPage)
    }
  }

  const handlePageInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitPageEdit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelPageEdit()
    }
  }

  const summaryText = hasResults
    ? `Showing ${start}-${end} of ${total}`
    : 'No datasets to display'

  return (
    <div className={cs.paginationBar}>
      <div className={cs.pageSizeControl}>
        <span className={cs.pageSizeLabel}>Rows per page</span>
        <Select<PageSizeOption>
          items={PAGE_SIZE_SELECT_OPTIONS}
          itemKey='key'
          itemLabelKey='label'
          activeItem={activeOption}
          onChange={option => onPageSizeChange(option.value)}
          triggerClassName={cs.pageSizeSelectTrigger}
          className={cs.pageSizeSelect}
        />
      </div>
      <div className={cs.paginationControls}>
        <span className={cs.pageSummary}>{summaryText}</span>
        <div className={cs.pageNavigation}>
          <Button
            label=''
            IconComponent={LeftArrowIcon}
            variant='minimal'
            className={cs.paginationButton}
            onClick={handlePrevious}
            disabled={displayPage <= 1}
          />
          {isEditingPage ? (
            <div className={cs.pageInlineEditor}>
              <span className={cs.pageIndicatorLabel}>Page</span>
              <input
                ref={pageInputRef}
                type='number'
                min={1}
                max={totalPages}
                className={cs.pageInlineInput}
                value={pageInputValue}
                onChange={event => {
                  setPageInputValue(event.target.value)
                }}
                onKeyDown={handlePageInputKeyDown}
                onBlur={cancelPageEdit}
                aria-label='Page number'
              />
              <span className={cs.pageIndicatorTotal}>of {totalPages}</span>
            </div>
          ) : (
            <button
              type='button'
              className={cs.pageIndicator}
              onClick={handlePageIndicatorClick}
              disabled={!hasResults}
            >
              Page {hasResults ? displayPage : 1} of {hasResults ? totalPages : 1}
            </button>
          )}
          <Button
            label=''
            IconComponent={RightArrowIcon}
            variant='minimal'
            className={cs.paginationButton}
            onClick={handleNext}
            disabled={!hasResults || displayPage >= totalPages}
          />
        </div>
      </div>
    </div>
  )
}
